import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { NominationSaveStep3 } from '../../types/nominationApi';
import type { CriminalSaveStep4 } from '../../types/criminalApi';
import type { NominationWizardDraft } from '../../types/nominationCaseRequest';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { useNominationLookups } from '../wizard/nomination/useNominationLookups';
import { apiOrLookupLabel, deliveryMethodDisplay, submitterFromApiStep } from './viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../wizard/criminal/CriminalReviewUi';
import { ConnectedAgdFileRefReviewField } from '../../components/view-request/ConnectedAgdFileRefReviewField';

type Props = {
  caseRequestId: string;
  draft: NominationWizardDraft;
  submittedAt: string;
  step3?: NominationSaveStep3;
};

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

export default function ViewRequestNominationCaseDetailsTab({ caseRequestId, draft, submittedAt, step3 }: Props) {
  const { t } = useTranslation();
  const noData = t('common.noDataFound');
  const lookups = useNominationLookups('review');

  const deliveryLabel = useMemo(
    () => deliveryMethodDisplay(
      draft as unknown as CriminalWizardDraft,
      lookups.deliveryMethods,
      step3 as CriminalSaveStep4 | undefined,
      noData,
    ),
    [draft, lookups.deliveryMethods, step3, noData],
  );

  const submitter = useMemo(() => submitterFromApiStep(step3), [step3]);

  return (
    <div className="vcr-case-details">
      <p className="vcr-submitted-at">
        Request Submitted at <strong>{formatDisplayDateTime(submittedAt) || '—'}</strong>
      </p>

      <ReviewSection step="Step 01" title="Case Overview">
        <ReviewSubBlock title="Case Categorization">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Case Intake Details">
          <ReviewField label="Description" value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField label="Case Number" value={draft.caseNumber} />
            <ReviewField label="Are there any connected matters (Cases)?" value={yesNoLabel(draft.hasConnectedMatters)} />
            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <ReviewField label="Connected Case Number" value={draft.connectedCaseNumber} />
                <ConnectedAgdFileRefReviewField
                  caseRequestId={caseRequestId}
                  label="Connected AGD File Reference Number"
                  fileRef={draft.connectedAgdFileRef}
                />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Timeline">
        <ReviewSubBlock title="Timeline">
          <ReviewGrid cols={3}>
            <ReviewField
              label="Urgency Level"
              value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
            />
            <ReviewField label="Deadline (if any)" value={formatDisplayDate(draft.deadline)} />
            <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Submission & Physical File Delivery">
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Submitted Agency Name" value={submitter.agency} />
            <ReviewField label="Designation" value={submitter.designation} />
            <ReviewField label="Submitted by" value={submitter.name} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label="Email Address" value={submitter.email} />
            <ReviewField label="Contact Number" value={submitter.contact} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Physical File Delivery Method">
          <ReviewField label="Delivery Method" value={deliveryLabel} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>
    </div>
  );
}
